/* import __COLOCATED_TEMPLATE__ from './plot-area.hbs'; */
/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes */
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { scaleLinear } from 'd3-scale';
import { format } from 'd3-format';
import { select, pointer } from 'd3-selection';
import { modifier } from 'ember-modifier';

import Zoom from '../utils/graphs/zoom';

let leftLabelOffset = 45,
  bottomLabelOffset = 50,
  hoverBox = [82, 24];

let fixMinus = function (numericString) {
  return numericString.replace('\u2212', '-');
};

let buildPolygon = function (lineItem, geometry) {
  // Since the output of the graphs is a closed polygon we just compute for the points
  let result = null;
  let points = '';
  for (let i = 0; i < lineItem.length; i++) {
    if (points.length > 0) points += ' ';
    points += geometry.xScale(lineItem[i][0]) + ',' + geometry.yScale(lineItem[i][1]);
  }
  result = { points: points };
  return result;
};

let buildIntersection = function (lineItem, colorItem, geometry) {
  let result = [];
  for (let i = 0; i < lineItem.length; i++) {
    let x = lineItem[i][0];
    let y = lineItem[i][1];
    if (
      x >= geometry.limits.xmin &&
      x <= geometry.limits.xmax &&
      y >= geometry.limits.ymin &&
      y <= geometry.limits.ymax
    ) {
      result.push({ x: x, y: y, cx: geometry.xScale(x), cy: geometry.yScale(y), color: colorItem });
    }
  }
  return result;
};

////////////////////////////////////////////////////////////////////////////////////
// plot-area - Section containing Diamond Graph as well as Graph Help and Legend  //
////////////////////////////////////////////////////////////////////////////////////

export default class PlotArea extends Component {
  tagName = '';
  @service intl;
  chartContainer = null;
  svgElement = null;
  zoom = Zoom.create();
  width = null;
  height = null;
  leftMargin = null;
  topMargin = null;
  rightMargin = null;
  bottomMargin = null;
  yTitle = null;
  isSelectionActive = null;
  showReturnTo100 = null;
  hover = null;
  coordinates = null;
  zoomLevel = null;
  boundResizeListener = null;
  boundMouseUpListener = null;
  openHelp = null;
  @service('aditools-analytics') analytics;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  constructor() {
    super(...arguments);

    this.set('yTitle', this.intl.t('plotYLabel'));
    if (this.width === null) {
      this.set('width', 540);
    }
    if (this.height === null) {
      this.set('height', 540);
    }
    if (this.leftMargin === null) {
      this.set('leftMargin', 60);
    }
    if (this.topMargin === null) {
      this.set('topMargin', 10);
    }
    if (this.rightMargin === null) {
      this.set('rightMargin', 15);
    }
    if (this.bottomMargin === null) {
      this.set('bottomMargin', 65);
    }
    if (this.isSelectionActive === null) {
      this.set('isSelectionActive', false);
    }
    if (this.showReturnTo100 === null) {
      this.set('showReturnTo100', false);
    }
    if (this.coordinates === null) {
      this.set('coordinates', { display: false, x: 0, y: 0, text: '' });
    }
    if (this.zoomLevel === null) {
      this.set('zoomLevel', 0);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Modifiers                                                                  //
  ////////////////////////////////////////////////////////////////////////////////
  /* eslint-disable no-unused-vars */
  returnZoomToDefault = modifier(
    (
      element,
      [
        _selectedPart,
        _selectedGainInput,
        _currentSignalType,
        _vDiffLeft,
        _vDiffRight,
        _vDiffSlider,
        _varTypeLeft,
        _varTypeRight,
        _varTypeSlider,
        _vref,
        _vsNeg,
        _vsPos,
      ],
    ) => {
      /* eslint-enable no-unused-vars */
      if (this.zoomLevel > 0) {
        this.returnTo100();
      } else {
        this.set('limits', this.axesDefaults);
      }
    },
  );

  ////////////////////////////////////////////////////////////////////////////////
  // Properties                                                                 //
  ////////////////////////////////////////////////////////////////////////////////
  get limits() {
    return this._limits ?? this.axesDefaults;
  }
  set limits(value) {
    this._limits = value;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Computed Values                                                            //
  ////////////////////////////////////////////////////////////////////////////////

  @computed('validationResults.isAllValid') get isValid() {
    return this.validationResults.isAllValid;
  }

  @computed('width') get graphWidth() {
    return this.width;
  }

  @computed('height') get graphHeight() {
    return this.height;
  }

  @computed('width') get chartWidth() {
    return this.width - (this.leftMargin + this.rightMargin);
  }

  @computed('height') get chartHeight() {
    return this.height - (this.topMargin + this.bottomMargin);
  }

  @computed('isDiffPGIASchematic', 'isDiff3AmpPGIASchematic') get xTitle() {
    return this.isDiffPGIASchematic
      ? this.isDiff3AmpPGIASchematic
        ? this.intl.t('plotXLabelDiff3Amp')
        : this.intl.t('plotXLabelDiff')
      : this.intl.t('plotXLabel');
  }

  @computed('chartWidth', 'chartHeight') get xTitleCenter() {
    return { x: this.chartWidth / 2, y: this.chartHeight + bottomLabelOffset };
  }

  @computed('chartHeight') get yTitleCenter() {
    return { x: -leftLabelOffset, y: this.chartHeight / 2 };
  }

  @computed('limits', 'chartWidth', 'chartHeight') get geometry() {
    let geometry = null;
    if (this.limits) {
      let width = this.chartWidth;
      let height = this.chartHeight;
      geometry = {
        width: width,
        height: height,
        limits: {
          xmin: this.limits.xmin,
          xmax: this.limits.xmax,
          ymin: this.limits.ymin,
          ymax: this.limits.ymax,
        },
        tickcount: [this.limits.xticks, this.limits.yticks],
        xScale: scaleLinear().domain([this.limits.xmin, this.limits.xmax]).range([0, width]),
        yScale: scaleLinear().domain([this.limits.ymin, this.limits.ymax]).range([height, 0]),
      };
    }
    return geometry;
  }

  @computed('geometry') get xTicks() {
    let result = null;
    let geometry = this.geometry;
    if (geometry) {
      let count = this.showReturnTo100 ? geometry.xScale.ticks().length : geometry.tickcount[0];
      let xFormatter = format(this.zoomLevel > 1 ? '.2f' : this.zoomLevel == 1 ? '.1f' : '.0f');
      result = geometry.xScale
        .ticks(count)
        .map((tick) => ({ value: fixMinus(xFormatter(tick)), x: geometry.xScale(tick), y: geometry.height }));
    }
    return result;
  }

  @computed('geometry') get yTicks() {
    let result = null;
    let geometry = this.geometry;
    if (geometry) {
      let count = this.showReturnTo100 ? geometry.yScale.ticks().length : geometry.tickcount[1];
      let yFormatter = format(this.zoomLevel > 1 ? '.2f' : this.zoomLevel == 1 ? '.1f' : '.0f');
      result = geometry.yScale
        .ticks(count)
        .map((tick) => ({ value: fixMinus(yFormatter(tick)), x: 0, y: geometry.yScale(tick) }));
    }
    return result;
  }

  @computed('chartWidth', 'chartHeight') get zoomButtonProps() {
    let buttonWidth = 132,
      buttonHeight = 24,
      buttonXpos = this.chartWidth - (buttonWidth + 10),
      buttonYpos = 10,
      textXpos = buttonXpos + buttonWidth / 2,
      textYpos = buttonYpos + 17;

    return {
      button: {
        x: buttonXpos,
        y: buttonYpos,
        w: buttonWidth,
        h: buttonHeight,
      },
      text: {
        x: textXpos,
        y: textYpos,
      },
    };
  }

  @computed('geometry', 'lines', 'colors') get polygons() {
    let result = [];
    let geometry = this.geometry;
    if (this.lines) {
      let lines = this.lines,
        colors = this.colors;
      if (lines && lines.length > 1) {
        // This is the outermost box
        let item = null;
        if (lines[0][0].length == 1) {
          let pointsArray = [
            [geometry.limits.xmin, geometry.limits.ymin],
            [geometry.limits.xmin, geometry.limits.ymax],
            [geometry.limits.xmax, geometry.limits.ymax],
            [geometry.limits.xmax, geometry.limits.ymin],
            [geometry.limits.xmin, geometry.limits.ymin],
          ];
          item = buildPolygon(pointsArray, geometry);
          if (item) {
            result.push({ points: item.points, class: 'grayfillpoly' });
          }
        }
        // This is the Polygon that was formed by the intersecting lines
        item = buildPolygon(lines[1], geometry);
        if (item) {
          result.push({ points: item.points, class: 'whitefillpoly' });
        }
        // Get the other Polygons
        for (let i = 2; i < lines.length; i++) {
          let polygonClass = '';
          item = buildPolygon(lines[i], geometry);
          switch (colors[i]) {
            case '#AA3939':
              polygonClass = 'redstrokeline';
              break;
            case '#D4986A':
              polygonClass = 'orangestrokeline';
              break;
            case '#88CC88':
              polygonClass = 'greenstrokeline';
              break;
            case '#669999':
              polygonClass = 'bluestrokeline';
              break;
            case '#800080':
              polygonClass = 'purplestrokeline';
              break;
          }
          if (item) {
            result.push({ points: item.points, class: polygonClass });
          }
        }
      }
    }
    return result;
  }

  @computed('geometry', 'lines', 'colors') get hoverPoints() {
    let result = [];
    let geometry = this.geometry;
    if (this.lines) {
      let lines = this.lines,
        colors = this.colors;
      if (lines && lines.length > 1) {
        // Get the intercepts from the other Polygons
        for (let i = 2; i < lines.length; i++) {
          let item = buildIntersection(lines[i], colors[i], geometry);
          if (item.length > 0) {
            result = result.concat(item);
          }
        }
        // get the intercept points of the all the polygons and insert them into the list
        // We also need to disregard if a previous point already exists
        let polygonPoints = buildIntersection(lines[1], '#000000', geometry);
        for (let j = 0; j < polygonPoints.length; j++) {
          let addPoint = true;
          for (let k = 0; k < result.length; k++) {
            if (polygonPoints[j].x === result[k].x && polygonPoints[j].y === result[k].y) {
              addPoint = false;
              break;
            }
          }
          if (addPoint) {
            result.push(polygonPoints[j]);
          }
        }
      }
      for (let i = 0; i < result.length; i++) {
        result[i].idx = i;
      }
    }
    return result;
  }

  @computed('validationResults', 'polygons') get isNotInOperatingRange() {
    let isAllValid = this.validationResults.isAllValid,
      isOperatingRange = this.validationResults.isOperatingRange;
    return isAllValid && !isOperatingRange;
  }

  @computed('chartWidth', 'chartHeight') get exclamationPoint() {
    return { x: this.chartWidth / 2 - 22.5, y: this.chartHeight / 2 + 50 };
  }

  @computed('hover', 'isSelectionActive') get showHover() {
    var hover = this.hover;
    var isSelectionActive = this.isSelectionActive;
    return hover && !isSelectionActive;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // User Functions                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  displayCoordinates(event, obj) {
    let mousePos = pointer(event, obj);
    let geometry = this.geometry;
    let xCoord = geometry.xScale.invert(mousePos[0]);
    let yCoord = geometry.yScale.invert(mousePos[1]);
    let xpos = this.chartWidth - 85;
    let ypos = this.chartHeight - 30;
    this.set('coordinates', {
      display: true,
      x: xpos,
      y: ypos,
      text: format('.2f')(xCoord) + ', ' + format('.2f')(yCoord),
    });
  }

  clearCoordinates(event, obj) {
    let mousePos = pointer(event, obj);
    let geometry = this.geometry;
    let xCoord = geometry.xScale.invert(mousePos[0]);
    let yCoord = geometry.yScale.invert(mousePos[1]);
    if (
      xCoord < geometry.limits.xmin ||
      xCoord > geometry.limits.xmax ||
      yCoord < geometry.limits.ymin ||
      yCoord > geometry.limits.ymax
    ) {
      this.set('coordinates', { display: false, x: 0, y: 0, text: '' });
    }
  }

  resizeGraph() {
    let elem = this.chartContainer;
    let isDiamondPlot = document.querySelector('#diamond-graph-drawing');

    if (isDiamondPlot) {
      this.set('width', elem.clientWidth - 5);
      this.set('height', elem.clientWidth - 5); //use width to keep the graph a square, height is not responsive due to vertical scrollbar
    }
  }

  addResizeListener() {
    this.boundResizeListener = this.resizeGraph.bind(this);
    window.addEventListener('resize', this.boundResizeListener);
  }

  removeResizeListener() {
    window.removeEventListener('resize', this.boundResizeListener);
  }

  mouseUpOutsideGraph() {
    if (this.isSelectionActive) {
      this.handleMouseUp();
    }
  }

  returnTo100() {
    this.zoom.returnTo100();

    this.set('limits', this.axesDefaults);
    this.set('showReturnTo100', false);
    this.set('zoomLevel', 0);
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  @action insertingChart(elem) {
    this.chartContainer = elem;
    this.resizeGraph();
    this.addResizeListener();
  }

  @action destroyingChart() {
    this.removeResizeListener();
    this.chartContainer = null;
  }

  @action insertingSVG(elem) {
    this.svgElement = elem;
    let self = this;

    this.boundMouseUpListener = this.mouseUpOutsideGraph.bind(this);
    window.addEventListener('mouseup', this.boundMouseUpListener);

    let svg = select('#diamond-graph-drawing');
    svg.on('mousemove', function (event) {
      self.displayCoordinates(event, this);
    });
    svg.on('mouseout', function (event) {
      self.clearCoordinates(event, this);
    });
  }

  @action destroyingSVG() {
    window.removeEventListener('mouseup', this.boundMouseUpListener);
    this.svgElement = null;
  }

  @action handleMouseOver(evt) {
    if (evt.target && evt.target.className.baseVal && evt.target.className.baseVal.indexOf('intercept') >= 0) {
      let hoverPoint = this.hoverPoints[evt.target.getAttribute('idx')];
      let xVal = format('.2f')(hoverPoint.x);
      let yVal = format('.2f')(hoverPoint.y);
      let x = parseFloat(hoverPoint.cx);
      let y = parseFloat(hoverPoint.cy);
      let boxX = Math.max(0, x - hoverBox[0] / 2);
      this.set('hover', {
        cx: hoverPoint.cx,
        cy: hoverPoint.cy,
        color: hoverPoint.color,
        tooltipX: boxX,
        tooltipY: Math.max(0, y - hoverBox[1] - 5),
        tooltipWidth: hoverBox[0],
        tooltipHeight: hoverBox[1],
        tooltipText: xVal + ', ' + yVal,
      });
    }
  }

  @action handleMouseOut(evt) {
    if (
      evt.target &&
      (!evt.target.className.baseVal ||
        (evt.target.className.baseVal && evt.target.className.baseVal.indexOf('intercept') < 0))
    ) {
      this.set('hover', null);
    }
  }

  @action handleMouseDown(evt) {
    if (evt.which === 1 || evt.which === 2 || evt.which === 3) {
      let elem = this.svgElement.querySelector('.box');
      let selectionStartPoint = this.zoom.startZoomSelect(evt, elem);
      if (selectionStartPoint) {
        let xTicks = this.xTicks;
        let yTicks = this.yTicks;
        let limits = this.geometry.limits;
        let zoomLimits = { minX: limits.xmin, maxX: limits.xmax, minY: limits.ymin, maxY: limits.ymax };
        this.set('zoom.chartHeight', this.chartHeight);
        this.set('zoom.limits', zoomLimits);
        this.set('zoom.xTicks', xTicks);
        this.set('zoom.yTicks', yTicks);
        this.set('isSelectionActive', true);
      }
    }
  }

  @action handleMouseMove(evt) {
    if (this.isSelectionActive) {
      this.zoom.moveZoomSelect(evt);
    }
  }

  @action handleMouseUp() {
    let selectionStartPoint = this.zoom.selectionStartPoint;
    let selectionDimension = this.zoom.selectionDimension;
    if (
      selectionDimension &&
      selectionStartPoint &&
      selectionDimension.width >= 15 &&
      selectionDimension.height >= 15
    ) {
      this.zoom.stopZoomSelect();
      let zoomLimits = this.zoom.zoomLimits;
      let limits = {
        xmin: zoomLimits.minX,
        xmax: zoomLimits.maxX,
        xticks: 6,
        ymin: zoomLimits.minY,
        ymax: zoomLimits.maxY,
        yticks: 6,
      };
      this.set('limits', limits);
      this.set('showReturnTo100', true);
      this.set('zoomLevel', this.zoomLevel + 1);
    }
    this.zoom.clearZoomSelection();
    this.set('isSelectionActive', false);
  }

  @action resetZoom() {
    this.returnTo100();
  }

  @action openShapesHelp() {
    this.analytics.setWebEvent(this.toolName, 'shapesHelpViewed', '');
    this.openHelp('help.importantShapes.header', 'help.importantShapes.message');
  }
}
